/**
 * ConfidenceSelectorV2 - Confidence level selection component
 *
 * Features:
 * - 5-level confidence scale
 * - Visual feedback with colors
 * - Keyboard shortcuts (Shift+1-5)
 * - Accessible design
 */

import { memo, useCallback } from 'react'
import { useChallengeV2 as useChallengeStoreV2 } from '../../../hooks/useChallenge'

const confidenceLevels = [
  {
    level: 1,
    label: 'Very Low',
    color: 'red',
    bgClass: 'bg-red-500',
    hoverClass: 'hover:bg-red-100 dark:hover:bg-red-900/30'
  },
  {
    level: 2,
    label: 'Low',
    color: 'orange',
    bgClass: 'bg-orange-500',
    hoverClass: 'hover:bg-orange-100 dark:hover:bg-orange-900/30'
  },
  {
    level: 3,
    label: 'Medium',
    color: 'yellow',
    bgClass: 'bg-yellow-500',
    hoverClass: 'hover:bg-yellow-100 dark:hover:bg-yellow-900/30'
  },
  {
    level: 4,
    label: 'High',
    color: 'emerald',
    bgClass: 'bg-emerald-500',
    hoverClass: 'hover:bg-emerald-100 dark:hover:bg-emerald-900/30'
  },
  {
    level: 5,
    label: 'Very High',
    color: 'green',
    bgClass: 'bg-green-500',
    hoverClass: 'hover:bg-green-100 dark:hover:bg-green-900/30'
  }
]

const ConfidenceSelectorV2 = memo(() => {
  // Store state
  const confidence = useChallengeStoreV2((state) => state.confidence)

  // Store actions
  const setConfidence = useChallengeStoreV2((state) => state.setConfidence)

  // Handle confidence change
  const handleConfidenceChange = useCallback(
    (level) => {
      setConfidence(level)
    },
    [setConfidence]
  )

  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Confidence</span>
        <span className="hidden sm:inline text-xs text-gray-400 dark:text-gray-500">
          (Shift+1-5)
        </span>
      </div>

      <div className="flex items-center gap-1.5">
        {confidenceLevels.map(({ level, label, bgClass, hoverClass }) => {
          const isSelected = confidence === level

          return (
            <button
              key={level}
              onClick={() => handleConfidenceChange(level)}
              className={`
                relative w-9 h-9 sm:w-10 sm:h-10 rounded-lg font-semibold text-sm
                transition-all duration-200 transform
                focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2
                ${
                  isSelected
                    ? `${bgClass} text-white shadow-md scale-105`
                    : `bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 ${hoverClass}`
                }
              `}
              title={`${label} confidence (Shift+${level})`}
              aria-label={`Set confidence to ${label}`}
              aria-pressed={isSelected}
            >
              {level}

              {/* Selection ring */}
              {isSelected && (
                <span className="absolute inset-0 rounded-lg ring-2 ring-offset-2 ring-offset-white dark:ring-offset-gray-800 ring-current opacity-50" />
              )}
            </button>
          )
        })}
      </div>

      {/* Current level label (mobile) */}
      <span className="sm:hidden text-xs text-gray-500 dark:text-gray-400">
        {confidenceLevels.find((c) => c.level === confidence)?.label || 'Medium'}
      </span>
    </div>
  )
})

ConfidenceSelectorV2.displayName = 'ConfidenceSelectorV2'

export default ConfidenceSelectorV2
